import React, { useCallback, useMemo } from 'react';
import { Column, Label, RowCentered, Text } from '@ydistri/ds';
import {
  AsoScenarioStoreTierConfigurationPatchRequest,
  AsoScenarioStoreTierConfigurationResponse,
  AsoScenarioStoreTierType,
} from '@ydistri/api-sdk';
import {
  useGetAsoScenarioStoreTierConfigurationsQuery,
  useUpdateAsoStoreTierConfigurationMutation,
} from '../../../apiAso';
import { useStableParams } from '../../../../../routes/useStableParams';
import { useAsoScenarioStatus } from '../../../hooks/useAsoScenarioStatus';
import { AsoValidationIssueMessages } from '../../../validation/AsoValidationIssueMessages';
import {
  getAsoValidationIssuesForStoreTierField,
  getAsoValidationIssueStatus,
  isAsoStoreTierRecomputeStateIssue,
  minimumStoreTierMultiplier,
} from '../../../validation/asoValidation';
import { useAsoValidation } from '../../../validation/useAsoValidation';
import {
  ConfigurationColumn,
  ConfigurationTable,
  DataCell,
  HeaderCell,
  RowHeaderCell,
  TierColumn,
} from './storeTierConfigurationStyledComponents';
import {
  formatPlaceholderValue,
  getConfigurationValue,
  getStoreTierLabel,
  StoreTierConfigurationInput,
} from './StoreTierConfigurationInput';
import AsoTooltip, { AsoTooltipKey } from '../../../AsoTooltip';

export type StoreTierConfigurationField = keyof AsoScenarioStoreTierConfigurationPatchRequest;

type StoreTierConfigurationKey = AsoScenarioStoreTierType | null;

interface StoreTierConfigurationData {
  field: StoreTierConfigurationField;
  label: string;
  precision: number;
  min?: number;
  tooltipKey: AsoTooltipKey;
}

const storeTierOrder: StoreTierConfigurationKey[] = [
  null,
  AsoScenarioStoreTierType.Tier1,
  AsoScenarioStoreTierType.Tier2,
  AsoScenarioStoreTierType.Tier3,
];

const configurationFields: StoreTierConfigurationData[] = [
  {
    field: 'tierMultiplier',
    label: 'Multiplier',
    precision: 1,
    min: minimumStoreTierMultiplier,
    tooltipKey: 'storeTierMultiplier',
  },
  {
    field: 'balancingMinimumInventoryMonths',
    label: 'Balancing Min mths.',
    precision: 0,
    min: 0,
    tooltipKey: 'balancingMinimumInventoryMonths',
  },
  {
    field: 'balancingMedianInventoryMonths',
    label: 'Balancing Median mths.',
    precision: 0,
    min: 0,
    tooltipKey: 'balancingMedianInventoryMonths',
  },
  {
    field: 'balancingMaximumInventoryMonths',
    label: 'Balancing Max mths.',
    precision: 0,
    min: 0,
    tooltipKey: 'balancingMaximumInventoryMonths',
  },
];

const StoreTierConfiguration: React.FC = () => {
  const { asoScenarioId } = useStableParams();
  const { isEditable } = useAsoScenarioStatus();
  const { issuesByArea } = useAsoValidation();
  const storeTierIssues = issuesByArea.storeTiers;
  const {
    data: configurations,
    isLoading,
    isError,
  } = useGetAsoScenarioStoreTierConfigurationsQuery(asoScenarioId ?? 0, {
    skip: !asoScenarioId,
  });
  const [updateStoreTierConfiguration] = useUpdateAsoStoreTierConfigurationMutation();

  const storeTierConfiguration = useMemo(() => {
    const map = new Map<StoreTierConfigurationKey, AsoScenarioStoreTierConfigurationResponse>();

    configurations?.forEach(configuration => {
      map.set(configuration.storeTier ?? null, configuration);
    });

    return map;
  }, [configurations]);

  const defaultConfiguration = storeTierConfiguration.get(null);
  const tableIssues = useMemo(
    () =>
      storeTierIssues.filter(
        issue => isAsoStoreTierRecomputeStateIssue(issue) || issue.storeTier !== undefined,
      ),
    [storeTierIssues],
  );

  const handleChange = useCallback(
    (
      configuration: AsoScenarioStoreTierConfigurationResponse,
      field: StoreTierConfigurationField,
      value: number | null,
    ) => {
      if (!asoScenarioId || !isEditable) {
        return;
      }

      void updateStoreTierConfiguration({
        asoScenarioId,
        storeTier: configuration.storeTier ?? null,
        payload: {
          [field]: value,
        },
      });
    },
    [asoScenarioId, isEditable, updateStoreTierConfiguration],
  );

  const getCellIssues = useCallback(
    (storeTier: StoreTierConfigurationKey, field: StoreTierConfigurationField) =>
      getAsoValidationIssuesForStoreTierField(storeTierIssues, storeTier, field),
    [storeTierIssues],
  );

  if (isLoading) {
    return <Text>Loading tier settings...</Text>;
  }

  if (isError) {
    return <Text $type="danger">Failed to load tier settings.</Text>;
  }

  if (!configurations) {
    return null;
  }

  return (
    <Column $gap="0.5rem">
      <RowCentered $gap="0.25rem">
        <Label>Configuration by store tiers</Label>
        <AsoTooltip tooltipKey="storeTierConfiguration" />
      </RowCentered>
      <ConfigurationTable>
        <colgroup>
          <ConfigurationColumn />
          {storeTierOrder.map(storeTier => (
            <TierColumn key={storeTier ?? 'default'} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <HeaderCell scope="col" />
            {storeTierOrder.map(storeTier => (
              <HeaderCell key={storeTier ?? 'default'} scope="col" $isDefault={storeTier === null}>
                {getStoreTierLabel(storeTier)}
              </HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {configurationFields.map(({ field, label, precision, min, tooltipKey }) => (
            <tr key={field}>
              <RowHeaderCell scope="row">
                <RowCentered $gap="0.25rem">
                  <span>{label}</span>
                  <AsoTooltip tooltipKey={tooltipKey} />
                </RowCentered>
              </RowHeaderCell>
              {storeTierOrder.map(storeTier => {
                const configuration = storeTierConfiguration.get(storeTier);
                const cellIssues = getCellIssues(storeTier, field);
                const validationStatus = getAsoValidationIssueStatus(cellIssues);
                const isDefaultColumn = storeTier === null;
                const defaultValue = defaultConfiguration
                  ? getConfigurationValue(defaultConfiguration, field)
                  : undefined;

                if (!configuration) {
                  return (
                    <DataCell
                      key={`${storeTier ?? 'default'}-${field}`}
                      $isDefault={isDefaultColumn}
                    >
                      <Text>-</Text>
                    </DataCell>
                  );
                }

                const value = getConfigurationValue(configuration, field);
                const overwritten =
                  !isDefaultColumn &&
                  defaultValue !== null &&
                  defaultValue !== undefined &&
                  value !== null &&
                  value !== undefined;
                const placeholder = !isDefaultColumn
                  ? formatPlaceholderValue(defaultValue, precision)
                  : undefined;

                return (
                  <DataCell key={`${storeTier ?? 'default'}-${field}`} $isDefault={isDefaultColumn}>
                    <StoreTierConfigurationInput
                      disabled={!isEditable}
                      precision={precision}
                      min={min}
                      overwritten={overwritten}
                      placeholder={placeholder}
                      status={validationStatus}
                      value={value}
                      onCommit={value => handleChange(configuration, field, value)}
                    />
                  </DataCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </ConfigurationTable>
      <AsoValidationIssueMessages issues={tableIssues} />
    </Column>
  );
};

export default StoreTierConfiguration;
